import { Button, Grid, message, Popconfirm, Table } from 'antd';
import { ColumnType, TablePaginationConfig } from 'antd/es/table';
import { RecordCountBadge } from 'components';
import { StyledTableHeader, StyledTableWrapper } from 'components/table/table.styled';
import { EventsFilterFormPaginated } from 'features/events';
import { TransectionApiServices } from 'features/transaction_list/api/transection-api.service';
import { Dispatch, SetStateAction, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

const { useBreakpoint } = Grid;

type EventsTableProps = {
    isLoading: boolean;
    data?: any;
    tableParams: EventsFilterFormPaginated;
    updateParams: Dispatch<SetStateAction<EventsFilterFormPaginated>>;
    refetch: any;
    setCouponAmount: any;
    setCouponNo: any;
    setCouponCode: any;
};

interface TeamGroup {
    groupKey: string;
    displayName: string;
    participants: any[];
    teamMin: number | null;
}


const getTeamName = (participant: any): string => {
    const val =
        participant?.['Team Name'] ??
        participant?.['team_name'] ??
        participant?.team_name ??
        participant?.TeamName;

    if (!val || typeof val === 'object' || typeof val !== 'string') return '';

    return val.toString();
};

const getCorrelationId = (participant: any): string => {
    return (
        participant?.['corelation_id'] ??
        participant?.corelation_id ??
        ''
    ).toString().trim();
};

const getTeamMin = (participant: any): number | null => {
    const val =
        participant?.['TeamMin'] ??
        participant?.['team_min'] ??
        participant?.team_min ??
        participant?.TeamMin ??
        participant?.['Team Min'];
    if (val === undefined || val === null || val === '') {

        return null;
    }
    const parsed = parseInt(val, 10);

    return isNaN(parsed) ? null : parsed;
};

const getParticipantPrice = (participant: any): number => {
    const val =
        participant?.['Category Price'] ??
        participant?.['category_price'] ??
        participant?.category_price ??
        participant?.price ??
        participant?.Price ??
        0;
    const num = parseFloat(val);

    return isNaN(num) ? 0 : num;
};

const UploadDetailsComponentTable = ({
    data,
    isLoading,
    tableParams,
    updateParams,
    refetch,
    setCouponAmount,
    setCouponNo,
    setCouponCode,
}: EventsTableProps) => {
    const handleTableChange = (pagination: TablePaginationConfig) => {
        updateParams({
            ...tableParams,
            page: pagination.current,
            size: pagination.pageSize,
        });
    };

    const screens = useBreakpoint();
    const tableWidth = screens.xxl ? { x: 1440 } : { x: 1200 };
    const { id } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const origin = searchParams.get('origin') || '';
    const eventId = searchParams.get('eventId');

    const isBulkUpload =
        origin.toLowerCase() === 'bulkupload' ||
        origin.toLowerCase() === 'bulk_upload' ||
        origin.toLowerCase() === 'bulk upload' ||
        location.pathname.toLowerCase().includes('bulk-upload');

    const originType = isBulkUpload ? 'bulkupload' : 'groupregistration';

    const participants = data?.items?.[0]?.ParticipantList_new || [];
    const participantList = data?.items?.[0]?.ParticipantList_new || [];
    const paymentStatus = data?.items?.[0]?.bulk_upload_details?.payment_Status;
    const iSColsed = data?.items?.[0]?.bulk_upload_details;

    const [isRemovingTeam, setIsRemovingTeam] = useState<string | null>(null);

    const onDelete = async (participantId: any, event_id: any) => {
        try {
            const apires: any = await TransectionApiServices().removeParticipantlist(
                originType,
                participantId,
                id,
                event_id
            );
            if (apires?.ResponseStatus === 0) {
                message.error(apires?.ResponseMessage);
            } else {
                message.success(apires?.ResponseMessage);
            }
            setCouponAmount(0);
            setCouponNo('');
            setCouponCode(null);
            refetch();
        } catch (error: any) {
            message.error(error?.message || error || 'Failed to remove participant');
        }
    };

    const onRemoveTeam = async (groupKey: string, teamParticipants: any[]) => {
        if (isRemovingTeam) return;
        setIsRemovingTeam(groupKey);

        try {
            let failureCount = 0;
            let successCount = 0;
            let lastErrorMessage = '';

            for (const p of teamParticipants) {
                try {
                    const apires: any = await TransectionApiServices().removeParticipantlist(
                        originType,
                        p.ParticipantId,
                        id,
                        p?.EventId || eventId
                    );
                    if (apires?.ResponseStatus === 0) {
                        failureCount++;
                        lastErrorMessage = apires?.ResponseMessage || 'Failed to remove participant';
                    } else {
                        successCount++;
                    }
                } catch (err: any) {
                    failureCount++;
                    lastErrorMessage = err?.message || 'Error removing participant';
                }
            }

            if (successCount > 0) {
                setCouponAmount(0);
                setCouponNo('');
                setCouponCode(null);
            }

            if (failureCount === 0) {
                message.success('Team removed successfully');
            } else if (successCount > 0) {
                message.warning(
                    `Removed ${successCount} participant(s), but ${failureCount} failed: ${lastErrorMessage}`
                );
            } else {
                message.error(lastErrorMessage || 'Failed to remove team');
            }

            refetch();
        } catch (error: any) {
            message.error(error?.message || 'Failed to remove team');
        } finally {
            setIsRemovingTeam(null);
        }
    };

    const allowByClosed =
        iSColsed?.is_closed_link === false &&
        iSColsed?.is_closed_by_admin === false;
    const baseHide =
        paymentStatus === 'SUCCESS' ||
        paymentStatus === 'PENDING' ||
        participantList.length === 0;

    const isRemovalDisabled = baseHide || !allowByClosed;

    const isExcludedKey = (key: string): boolean => {
        if (key === 'ParticipantId') return true;
        const normalized = key.toLowerCase().replace(/[\s_-]/g, '');
        if (normalized === 'corelationid' || normalized === 'correlationid') {

            return true;
        }

        return false;
    };

    const dynamicColumns: ColumnType<any>[] = participants.length
        ? Object.keys(participants[0])
            .filter((key) => !isExcludedKey(key))
            .map((key) => ({
                title: key,
                dataIndex: key,
                key: key,
                ellipsis: true,
                width: 200,
                render: (text: any) => text ?? '-',
            }))
        : [];

    const individualDynamicColumns: ColumnType<any>[] = participants.length
        ? Object.keys(participants[0])
            .filter((key) => {
                if (isExcludedKey(key)) return false;
                const normalized = key.toLowerCase().replace(/[\s_-]/g, '');
                if (normalized === 'teamname') return false;
                if (
                    normalized === 'teamsize' ||
                    normalized === 'teammin' ||
                    normalized === 'teammax'
                ) {

                    return false;
                }

                return true;
            })
            .map((key) => ({
                title: key,
                dataIndex: key,
                key: key,
                ellipsis: true,
                width: 200,
                render: (text: any) => text ?? '-',
            }))
        : [];

    // ==========================================
    // BULK UPLOAD PATH (STRICTLY UNCHANGED)
    // ==========================================
    if (isBulkUpload) {
        const bulkActionColumn: ColumnType<any> = {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this participant?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => {
                        if (onDelete) {
                            onDelete(record.ParticipantId, record?.EventId);
                        } else {
                            message.warning('Delete handler not implemented');
                        }
                    }}
                >
                    <span role="button" style={{ cursor: 'pointer', color: 'red' }}>
                        Remove
                    </span>
                </Popconfirm>
            ),
        };

        const bulkColumnsWithAction =
            baseHide || !allowByClosed
                ? dynamicColumns
                : [...dynamicColumns, bulkActionColumn];

        return (
            <div>
                <StyledTableWrapper>
                    <StyledTableHeader>
                        <RecordCountBadge
                            text="Participants found"
                            count={data?.items?.[0]?.ParticipantList_new?.length || 0}
                        />
                    </StyledTableHeader>

                    <Table
                        loading={isLoading}
                        dataSource={participants}
                        rowKey="ParticipantId"
                        onChange={handleTableChange}
                        pagination={{
                            current: tableParams.page,
                            defaultPageSize: tableParams.size,
                            total: data?.total,
                        }}
                        className="event_table"
                        scroll={{ ...tableWidth, x: true }}
                        columns={bulkColumnsWithAction}
                    />
                </StyledTableWrapper>
            </div>
        );
    }

    // ==========================================
    // GROUP REGISTRATION DETAIL GROUPING PATH
    // ==========================================
    const individualParticipants: any[] = [];
    const teamGroupsMap = new Map<string, TeamGroup>();

    participants.forEach((p: any) => {
        const rawTeamName = getTeamName(p);
        const trimmedTeamName = rawTeamName.trim();
        const correlationId = getCorrelationId(p);
        const pTeamMin = getTeamMin(p);

        const regType = (
            p?.['Registration Type'] ??
            p?.['registration_type'] ??
            p?.RegistrationType ??
            p?.registration_type ??
            p?.['Booking Type'] ??
            p?.booking_type ??
            ''
        ).toString().toLowerCase();

        const isTeam = regType.includes('team') || regType === '2';

        if (isTeam) {
            // Group by correlation_id (fallback to team name if correlation_id is not present)
            const groupKey = correlationId || trimmedTeamName || 'Team';
            if (!teamGroupsMap.has(groupKey)) {
                teamGroupsMap.set(groupKey, {
                    groupKey: groupKey,
                    displayName: trimmedTeamName || 'Team',
                    participants: [],
                    teamMin: pTeamMin,
                });
            }
            const group = teamGroupsMap.get(groupKey)!;
            group.participants.push(p);
            if (group.teamMin === null && pTeamMin !== null) {
                group.teamMin = pTeamMin;
            }
        } else {
            // If Registration Type is not team -> treat as Individual
            individualParticipants.push(p);
        }
    });

    const teamGroups = Array.from(teamGroupsMap.values());

    // Sort participants in each team so that candidate with price > 0 is on top
    teamGroups.forEach((group) => {
        group.participants.sort((a: any, b: any) => {
            const priceA = getParticipantPrice(a);
            const priceB = getParticipantPrice(b);

            return priceB - priceA;
        });
    });

    const getIndividualColumns = (): ColumnType<any>[] => {
        if (isRemovalDisabled) {

            return individualDynamicColumns;
        }

        const individualActionColumn: ColumnType<any> = {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this participant?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => onDelete(record.ParticipantId, record?.EventId)}
                >
                    <span role="button" style={{ cursor: 'pointer', color: 'red' }}>
                        Remove
                    </span>
                </Popconfirm>
            ),
        };

        return [...individualDynamicColumns, individualActionColumn];
    };

    const getTeamColumns = (
        team: TeamGroup,
        canRemoveIndividual: boolean
    ): ColumnType<any>[] => {
        if (isRemovalDisabled) {

            return dynamicColumns;
        }

        const teamActionColumn: ColumnType<any> = {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => {
                const participantPrice = getParticipantPrice(record);
                const hasPrice = participantPrice > 0;

                // Candidate with price in it has delete option disabled every time
                if (hasPrice) {

                    return (
                        <span
                            style={{ color: '#bfbfbf', cursor: 'not-allowed' }}
                            title="Primary team member with category price cannot be removed individually. Remove entire team instead."
                        >
                            Remove
                        </span>
                    );
                }

                if (canRemoveIndividual) {

                    return (
                        <Popconfirm
                            title="Are you sure you want to delete this participant?"
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => onDelete(record.ParticipantId, record?.EventId)}
                        >
                            <span role="button" style={{ cursor: 'pointer', color: 'red' }}>
                                Remove
                            </span>
                        </Popconfirm>
                    );
                }

                return (
                    <span
                        style={{ color: '#bfbfbf', cursor: 'not-allowed' }}
                        title={
                            team.teamMin !== null
                                ? `Minimum team size of ${team.teamMin} reached. Remove entire team instead.`
                                : 'Removal disabled'
                        }
                    >
                        Remove
                    </span>
                );
            },
        };

        return [...dynamicColumns, teamActionColumn];
    };

    const individualCount = individualParticipants.length;
    const teamParticipantsCount = teamGroups.reduce(
        (acc, t) => acc + t.participants.length,
        0
    );
    const teamsCount = teamGroups.length;

    const summaryParts: string[] = [];
    if (individualCount > 0) {
        summaryParts.push(
            `${individualCount} individual${individualCount === 1 ? '' : 's'}`
        );
    }
    if (teamParticipantsCount > 0) {
        summaryParts.push(
            `${teamParticipantsCount} across ${teamsCount} team${teamsCount === 1 ? '' : 's'}`
        );
    }
    const breakdownSummary = summaryParts.join(' · ');

    return (
        <div>
            <StyledTableWrapper>
                <StyledTableHeader>
                    <RecordCountBadge
                        text="Participants found"
                        count={participants.length}
                    />
                    {breakdownSummary && (
                        <span
                            style={{
                                color: '#8c8c8c',
                                fontSize: '13px',
                                fontWeight: 500,
                                alignSelf: 'center',
                            }}
                        >
                            {breakdownSummary}
                        </span>
                    )}
                </StyledTableHeader>

                {participants.length === 0 && (
                    <Table
                        loading={isLoading}
                        dataSource={[]}
                        rowKey="ParticipantId"
                        className="event_table"
                        scroll={{ ...tableWidth, x: true }}
                        columns={dynamicColumns}
                        pagination={false}
                    />
                )}

                {individualParticipants.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 14px',
                                backgroundColor: '#fafafa',
                                border: '1px solid #f0f0f0',
                                borderBottom: 'none',
                                borderRadius: '4px 4px 0 0',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span
                                    style={{
                                        backgroundColor: '#e6f7ff',
                                        color: '#1890ff',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    INDIVIDUAL
                                </span>
                                <span style={{ fontWeight: 500, fontSize: '14px', color: '#262626' }}>
                                    {individualParticipants.length}{' '}
                                    {individualParticipants.length === 1
                                        ? 'Participant'
                                        : 'Participants'}
                                </span>
                            </div>
                        </div>
                        <Table
                            loading={isLoading}
                            dataSource={individualParticipants}
                            rowKey="ParticipantId"
                            pagination={false}
                            className="event_table"
                            scroll={{ ...tableWidth, x: true }}
                            columns={getIndividualColumns()}
                        />
                    </div>
                )}

                {teamGroups.map((team) => {
                    const canRemoveIndividual =
                        !isRemovalDisabled &&
                        (team.teamMin === null || team.participants.length > team.teamMin);

                    return (
                        <div key={team.groupKey} style={{ marginBottom: 28 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 14px',
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #f0f0f0',
                                    borderBottom: 'none',
                                    borderRadius: '4px 4px 0 0',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <span
                                        style={{
                                            backgroundColor: '#fff0f6',
                                            color: '#eb2f96',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        TEAM
                                    </span>
                                    <span
                                        style={{
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            color: '#262626',
                                        }}
                                    >
                                        {team.displayName}
                                    </span>
                                    <span style={{ color: '#8c8c8c', fontSize: '13px' }}>
                                        ({team.participants.length}{' '}
                                        {team.participants.length === 1
                                            ? 'Participant'
                                            : 'Participants'}
                                        )
                                    </span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    {team.teamMin !== null && (
                                        <span
                                            style={{
                                                color: '#595959',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Minimum: <strong>{team.teamMin}</strong>
                                        </span>
                                    )}
                                    {!isRemovalDisabled && (
                                        <Popconfirm
                                            title={`Are you sure you want to remove team "${team.displayName}"?`}
                                            okText="Yes"
                                            cancelText="No"
                                            onConfirm={() =>
                                                onRemoveTeam(team.groupKey, team.participants)
                                            }
                                            disabled={isRemovingTeam === team.groupKey}
                                        >
                                            <Button
                                                danger
                                                size="small"
                                                loading={isRemovingTeam === team.groupKey}
                                                disabled={isRemovingTeam !== null}
                                                style={{ borderRadius: 4, fontWeight: 500 }}
                                            >
                                                Remove Team
                                            </Button>
                                        </Popconfirm>
                                    )}
                                </div>
                            </div>

                            <Table
                                loading={isLoading}
                                dataSource={team.participants}
                                rowKey="ParticipantId"
                                pagination={false}
                                className="event_table"
                                scroll={{ ...tableWidth, x: true }}
                                columns={getTeamColumns(team, canRemoveIndividual)}
                            />
                        </div>
                    );
                })}
            </StyledTableWrapper>
        </div>
    );
};

export default UploadDetailsComponentTable;

